<template>
  <div class="do-hours">
    <label class="d-label marg-1-top" v-if="isShowOptions">{{ i18n['period_hours'] }}</label>

    <DoDatePickerRange
      v-if="isShowOptions"
      :date="date"
      :startDate="startDate"
      :endDate="endDate"
      @datesChanged="setDates">
    </DoDatePickerRange>

    <!-- Checkbox kun for DK -->
    <div class="do-hours__controls" v-if="isDKSchool" @click.stop.prevent="setShowLessons(!showLessons)">
      <label class="d-label">{{ i18n['show_lessons'] }} ({{ subjectDefaultDuration }} min)</label>
        <AppToggle :propOn="showLessons"></AppToggle>
    </div>

    <Loader v-if="isLoading" :is-small="true" bg-color="#ffffff"></Loader>

    <template v-else>
      <div class="do-card--overflow" ref="dropdown" :style="{'maxHeight': maxHeight}">
        <h3 v-if="hoursGrouped.length < 1" class="text-explainer marg-1-top">
          {{ i18n['no_schedule'] }}
        </h3>

        <table v-else class="do-hours__table">
          <tbody>
          <template v-for="parentCat in hoursGroupedSorted">
            <tr class="do-hours__row do-hours__row--parent">
              <td class="do-hours__cell do-hours__cell--title">
                <strong>{{ parentCat.vTitle }}</strong>
              </td>
              <td class="do-hours__cell do-hours__cell--value">
                <strong>{{ parentCat.vHours }}</strong>
              </td>
              <td class="do-hours__cell do-hours__cell--lessons" v-if="showLessons">
                <strong>{{ parentCat.vLessons }}</strong>
              </td>
            </tr>

            <template v-for="subCat in parentCat.subCategories">
              <tr
                v-if="parentCat.subCategories.length > 1"
                class="do-hours__row do-hours__row--sub"
              >
                <td class="do-hours__cell do-hours__cell--title">{{ subCat.vTitle }}</td>
                <td class="do-hours__cell do-hours__cell--value">{{ subCat.vHours }}</td>
                <td class="do-hours__cell do-hours__cell--lessons" v-if="showLessons">
                  {{ subCat.vLessons }}
                </td>
              </tr>

              <template v-for="subject in subCat.subjects">
                <tr class="do-hours__row do-hours__row--subject">
                  <td class="do-hours__cell do-hours__cell--title">{{ subject.vTitle }}</td>
                  <td class="do-hours__cell do-hours__cell--value">{{ subject.vHours }}</td>
                  <td class="do-hours__cell do-hours__cell--lessons" v-if="showLessons">
                    {{ subject.vLessons }}
                  </td>
                </tr>
              </template>
            </template>
          </template>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import docendo from '../../docendo'
import moment from 'moment'
import Loader from '../common/Loader'
import DoDatePickerRange from '../datepicker/DoDatePickerRange'
import AppToggle from '../common/AppToggle.vue'

const I18N = {
  da: {
    flextime: 'Flextid',
    flexin: 'Flex ind',
    flexout: 'Flex ud',
    teaching: 'Undervisning',
    preparation: 'Forberedelse',
    additional_tasks: 'Øvrige Opgaver',
    available: 'Rådighedstimer',
    club: 'SFO tid',
    pool: 'Lokalpulje',
    absence: 'Fravær',
    other: 'Opgaver',
    subject: 'Fag',
    person_resource: 'Ressource',
    person_resource_visible: 'Synlig ressource',
    with_kids: 'Med børn',
    without_kids: 'Uden børn',
    unknown: 'Rådighedstime',
    break: 'Pause',
    period_hours: 'Timer i perioden',
    no_schedule: 'Intet skemalagt i perioden',
    hour: 'time',
    hours: 'timer',
    min: 'min',
    optional: 'Valgfag',
    resource: 'Ressource',
    show_lessons: 'Vis i lektioner'
  },
  en: {
    teaching: 'Teaching',
    preparation: 'Preparation',
    additional_tasks: 'Additional Tasks',
    available: 'Available Hours',
    club: 'Club Time',
    pool: 'Local Pool',
    absence: 'Absence',
    period_hours: 'Hours in the Period',
    no_schedule: 'No Schedule in the Period',
    other: 'Tasks',
    subject: 'Course',
    person_resource: 'Resource',
    person_resource_visible: 'Visible resource',
    with_kids: 'With children',
    without_kids: 'Without children',
    unknown: 'Free period',
    break: 'Break',
    hour: 'hour',
    hours: 'hours',
    min: 'min',
    optional: 'Optional',
    resource: 'Resource',
    show_lessons: 'Show lessons'
  },
  nb: {
    teaching: 'Undervisning',
    preparation: 'Forberedelse',
    additional_tasks: 'Ekstra oppgaver',
    available: 'Tilgjengelige timer',
    club: 'SFO tid',
    pool: 'Lokalpulje',
    absence: 'Fravær',
    period_hours: 'Timer i perioden',
    no_schedule: 'Ingen timeplan i perioden',
    other: 'Oppgaver',
    subject: 'Fag',
    person_resource: 'Ressurs',
    person_resource_visible: 'Synlig ressurs',
    with_kids: 'Med barn',
    without_kids: 'Uten barn',
    unknown: 'Ledig time',
    break: 'Pause',
    hour: 'time',
    hours: 'timer',
    min: 'min',
    optional: 'Valgfag',
    resource: 'Ressurs',
    show_lessons: 'Vis leksjoner'
  }
}

export default {
  components: {AppToggle, Loader, DoDatePickerRange},
  props: {
    schoolId: {default: 7272},
    calendarId: {default: 7},
    isShowOptions: { type: Boolean, default: true },
    date: {default: moment()}
  },
  data () {
    return {
      startDate: moment(this.date).startOf('isoWeek'),
      endDate: moment(this.date).endOf('isoWeek'),
      hours: [],
      isLoading: false,
      maxHeight: '50vh',
      showLessons: false
    }
  },
  computed: {
    school () {
      return this.$store.state.activeSchool
    },
    locale () {
      return this.$store.getters.locale
    },
    i18n () {
      return I18N[this.locale]
    },
    localPoolTitle () {
      const title = this.$store.getters.localPoolTitle
      return title || this.$t('vue2.task_overview.pool')
    },
    isDKSchool () {
      return this.school && this.school.country && this.school.country === 'DK'
    },
    subjectDefaultDuration () {
      return this.school && this.school.subject_default_duration ? this.school.subject_default_duration : 45
    },
    hoursGrouped () {
      let categories = []
      this.hours.forEach(subarray => {
        const masterType = subarray[0] ? subarray[0] : null
        const subjectType = subarray[1] ? subarray[1] : null
        const subjectTitle = subarray[2] ? subarray[2] : null
        const minutes = subarray[3] ? subarray[3] : 0
        const vHours = this.getMinutesToHours(minutes)
        const vLessons = this.getLessonsNumber(minutes)

        if (masterType) {
          const masterTypeIdx = categories.findIndex(cat => cat.masterType === masterType)
          if (masterTypeIdx < 0) {
            categories.push({
              masterType,
              vTitle: this.getTitle(masterType),
              vOrder: this.getOrder(masterType),
              vHours,
              vLessons,
              subCategories: []
            })
          } else {
            if (subjectType && !subjectTitle) {
              const subjectTypeIdx = categories[masterTypeIdx]['subCategories'].findIndex(subCat => subCat.subjectType === subjectType)
              const subjectTypeObj = {
                masterType,
                subjectType,
                vTitle: this.getTitle(subjectType),
                vHours,
                vLessons,
                vOrder: this.getOrder(subjectType),
                subjects: []
              }
              if (subjectTypeIdx < 0) {
                categories[masterTypeIdx]['subCategories'].push(subjectTypeObj)
              } else {
                categories[masterTypeIdx]['subCategories'][subjectTypeIdx].push(subjectTypeObj)
              }
            }
            if (subjectType && subjectTitle) {
              const subjectTypeIdx = categories[masterTypeIdx]['subCategories'].findIndex(subCat => subCat.subjectType === subjectType)
              const subjectObj = {
                masterType,
                subjectType,
                subjectTitle,
                vTitle: subjectTitle,
                vHours,
                vLessons
              }
              categories[masterTypeIdx]['subCategories'][subjectTypeIdx]['subjects'].push(subjectObj)
            }
          }
        }
      })
      return categories
    },
    hoursGroupedSorted () {
      const sorted = [...this.hoursGrouped]
      sorted.sort((a, b) => a.vOrder - b.vOrder)
      sorted.forEach(item => {
        if (item.subCategories) item.subCategories.sort((a, b) => a.vOrder - b.vOrder)
      })
      return sorted
    }
  },
  methods: {
    getTitle (label) {
      if (label === 'pool') {
        return this.localPoolTitle
      }
      return this.i18n[label] ? this.i18n[label] : label
    },
    getOrder (label) {
      const orders = {
        flextime: 1,
        teaching: 1,
        subject: 1,
        person_resource: 3,
        person_resource_visible: 2,
        with_kids: 1,
        without_kids: 2,
        additional_tasks: 2,
        other: 3,
        preparation: 4,
        available: 7,
        club: 6,
        pool: 5,
        unknown: 7,
        break: 2,
        absence: 12
      }
      return orders[label] ? orders[label] : 20
    },
    setDates (startAndEnd) {
      const start = startAndEnd.start
      const end = startAndEnd.end
      if (start) this.startDate = start
      if (end) this.endDate = end
      this.readHours()
    },
    readHours () {
      this.isLoading = true
      this.hours = []
      const start = moment(this.startDate).format('YYYY-MM-DD')
      const end = moment(this.endDate).format('YYYY-MM-DD')
      docendo.hoursCountForCalendar(this.schoolId, this.calendarId, start, end, success => {
        this.hours = JSON.parse(success)
        this.isLoading = false
      }, failure => console.log({failure}))
    },
    getMinutesToHours (minutes) {
      const total = Math.abs(minutes).toFixed(0)
      const h = Math.floor(total / 60)
      const m = total % 60
      const hLabel = h !== 1 ? this.i18n['hours'] : this.i18n['hour']
      const sign = minutes < 0 ? '-' : ''
      const hoursStr = h > 0 ? h + ' ' + hLabel + ' ' : ''
      return sign + hoursStr + m + ' ' + this.i18n['min']
    },
    getLessonsNumber (minutes) {
      if (!minutes) return '0.0'
      const total = Math.abs(minutes)
      const raw = total / this.subjectDefaultDuration
      const rounded = Math.round(raw * 10) / 10
      const sign = minutes < 0 ? '-' : ''
      return sign + rounded.toFixed(1)
    },
    calculateMaxHeight () {
      const pxFromTop = this.$refs.dropdown.getBoundingClientRect().top
      const maxHeight = window.innerHeight - (pxFromTop + 30)
      this.maxHeight = `${maxHeight}px`
    },
    setShowLessons (bool) {
      this.showLessons = bool
      const key = 'do_calculate_hours_as_lessons'
      if (bool) {
        this.$helpers.setLocalStorage(key, true)
        this.$store.dispatch('trackEvent', 'calendar_calculateHours_showAsLessons')
      } else {
        this.$helpers.removeLocalStorage(key)
      }
    }
  },
  beforeMount () {
    const key = 'do_calculate_hours_as_lessons'
    const isActivated = this.$helpers.getLocalStorage(key)
    if (isActivated && this.isDKSchool) {
      this.showLessons = true
    }
  },
  mounted () {
    this.calculateMaxHeight()
    window.addEventListener('resize', this.calculateMaxHeight)
    this.readHours()
    this.$store.dispatch('trackEvent', 'calendar_calculateHours_view')
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.calculateMaxHeight)
  }
}
</script>

<style scoped>
.do-hours {
  width: fit-content;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-bottom: 1rem;
}

.do-hours__controls {
  margin-top: 0.25rem;
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-end;
  cursor: pointer;
}

.do-hours__controls .d-label {
  line-height: 0.85rem;
}

.do-card--overflow {
  max-height: 70vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  border-radius: 4px;
  border: 1px solid #cbd6e0;
}

.do-hours__table {
  width: 100%;
  border-collapse: collapse;
}

.do-hours__table tbody td {
  padding-right: 4px;
}

.do-hours__row--parent {
  background: #f6f9fc;
  font-weight: bold;
  border-radius: 4px 0 0 4px;
  border-bottom: 1px solid #cbd6e0;
}

.do-hours__row--parent:not(:first-child) {
  border-top: 1px solid #cbd6e0;
}

.do-hours__row--parent > td:first-child {
  border-radius: 4px 0 0 0;
  padding: 0.5rem;
}

.do-hours__row--sub {
  background: #f9fafb;
  font-weight: bold;
  font-size: 0.9rem;
}

.do-hours__row--sub > td:first-child {
  padding-left: 1rem;
}

.do-hours__row--sub > td {
  border-top: 1px solid #cbd6e0;
  border-bottom: 1px solid #cbd6e0;
}

.do-hours__row--subject {
  color: #7a818c;
  font-size: 0.9rem;
}

.do-hours__row--subject > td {
  padding-left: 1.5rem;
}

.do-hours__cell--value {
  text-align: right;
}

.do-hours__cell--lessons {
  text-align: right;
  border-left: 1px solid #cbd6e0;
}
</style>



// WEBPACK FOOTER //
// CalculateHours.vue?704c1138